import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageSection, Spinner } from '@patternfly/react-core';
import {
    PencilAltIcon,
    UserIcon,
    CalendarAltIcon,
    BuildingIcon,
    TagIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    AutomationIcon,
    HistoryIcon,
    TrashIcon,
    SearchPlusIcon
} from '@patternfly/react-icons';
import { getAdById, reviewAd, reviewAdAction, getIterationHistory, deleteAd, AdSampleDto } from '@app/utils/utils';
import { getAdStateConfig, getAdTypeConfig, getGenerationStepConfig, getStepProgress, isActiveGenerationStep } from './adStateUtils';
import { EditAd } from './EditAd';
import toast from 'react-hot-toast';
import { AppModal } from '@app/components/AppModal';
import { GradientButton } from '@app/components/GradientButton';
import { LogoBadge } from '@app/components/LogoBadge';
import { LedCarPreview } from '@app/Ads/LedCarPreview';
import { colors } from '@app/components/designTokens';

interface IterationItem {
    iterationNumber: number;
    promptUsed: string;
    feedback: string | null;
    imageUrl: string | null;
    createdAt: string;
    samples?: AdSampleDto[];
}

interface IterationHistoryData {
    iterations: IterationItem[];
    currentIteration: number;
    maxIterations: number;
}

const AdDetailPage: React.FunctionComponent = () => {
    const { adId } = useParams<{ adId: string }>();
    const navigate = useNavigate();

    const [ad, setAd] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Review state
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [iterationData, setIterationData] = useState<IterationHistoryData | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [selectedHistoryImage, setSelectedHistoryImage] = useState<string | null>(null);
    // Multi-sample selection: which of the current round's samples is chosen.
    const [selectedSampleUri, setSelectedSampleUri] = useState<string | null>(null);
    // Lightbox preview of a past-round sample (enlarge before accepting).
    const [previewSample, setPreviewSample] = useState<{
        s3Uri: string;
        presignedUrl?: string;
        iterationNumber: number;
        sampleIndex: number;
    } | null>(null);

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Timestamp of the last regenerate request. Used as a grace window so the
    // progress bar shows and polling keeps running even if the backend hasn't
    // flipped the ad to IN_PROGRESS yet by the time we first re-read it.
    const [regenAt, setRegenAt] = useState<number | null>(null);
    const REGEN_GRACE_MS = 30000;

    const fetchAd = () => {
        if (!adId) return;
        setIsLoading(true);
        getAdById(adId).then((result) => {
            if (result) {
                setAd(result);
                setError(false);
            } else {
                setError(true);
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchAd();
    }, [adId]);

    // Poll while ad is in progress so generation steps update live. Also poll
    // during the grace window right after a regenerate request, since the
    // backend may not have transitioned to IN_PROGRESS yet.
    useEffect(() => {
        if (!ad) return;
        const inProg = ad.currentState === 'IN_PROGRESS' || ad.currentState === 'IN_PROGESS';
        const inGrace = regenAt !== null && Date.now() - regenAt < REGEN_GRACE_MS;
        if (!inProg && !inGrace) return;
        const interval = setInterval(() => {
            if (!adId) return;
            getAdById(adId).then((result) => {
                if (result) {
                    setAd(result);
                    // Once the backend confirms progress, drop the grace window
                    // so normal in-progress polling drives the lifecycle.
                    if (result.currentState === 'IN_PROGRESS' || result.currentState === 'IN_PROGESS') {
                        setRegenAt(null);
                    }
                }
            });
        }, 5000);
        return () => clearInterval(interval);
    }, [ad?.currentState, adId, regenAt]);

    const handleEditClick = () => {
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
    };

    const handleEditSave = () => {
        fetchAd();
    };

    // Fetch iteration history for AI-generated ads
    useEffect(() => {
        if (ad?.adType === 'GENERATE_WITH_AI' && ad?.adId) {
            getIterationHistory(ad.adId)
                .then((data: IterationHistoryData) => {
                    if (data) setIterationData(data);
                })
                .catch(() => {});
        }
    }, [ad?.adType, ad?.adId, ad?.currentState]);

    // Auto-select the first sample when a new round's samples arrive (or keep the
    // current selection if it's still present).
    useEffect(() => {
        const uris: string[] = Array.isArray(ad?.samples) ? ad.samples.map((s: AdSampleDto) => s.s3Uri) : [];
        if (uris.length === 0) {
            if (selectedSampleUri !== null) setSelectedSampleUri(null);
        } else if (!selectedSampleUri || !uris.includes(selectedSampleUri)) {
            setSelectedSampleUri(uris[0]);
        }
    }, [ad?.samples]);

    const handleFeedbackChange = (value: string) => {
        const sanitized = value.replace(/<[^>]*>/g, '');
        const limited = sanitized.slice(0, 1000);
        setFeedback(limited);
    };

    const samples: AdSampleDto[] = (ad && Array.isArray(ad.samples)) ? ad.samples : [];
    const hasSamples = samples.length > 0;

    const handleApprove = async () => {
        if (!ad) return;
        if (hasSamples && !selectedSampleUri) {
            toast.error('Pick a sample to approve');
            return;
        }
        setIsApproving(true);
        try {
            if (hasSamples) {
                await reviewAdAction(ad.adId, 'APPROVE', { chosenSampleS3Uri: selectedSampleUri! });
            } else {
                await reviewAd(ad.adId, true);
            }
            toast.success('Ad approved!');
            fetchAd();
        } catch (error: any) {
            console.error('Failed to approve ad:', error);
            toast.error(error.message || 'Failed to approve ad');
        } finally {
            setIsApproving(false);
        }
    };

    // Accept a sample from an earlier round straight out of the Iteration
    // History timeline. The backend already validates any sample s3Uri belongs
    // to the ad, so this reuses the normal APPROVE path — no extra round.
    const handleApproveRevision = async (
        s3Uri: string,
        iterationNumber: number,
        opts?: { skipConfirm?: boolean }
    ) => {
        if (!ad) return;
        if (!opts?.skipConfirm && !window.confirm(`Accept Revision #${iterationNumber}? This will publish that image.`)) {
            return;
        }
        setIsApproving(true);
        try {
            await reviewAdAction(ad.adId, 'APPROVE', { chosenSampleS3Uri: s3Uri });
            toast.success('Ad approved!');
            setPreviewSample(null);
            fetchAd();
        } catch (error: any) {
            console.error('Failed to approve revision:', error);
            toast.error(error.message || 'Failed to approve revision');
        } finally {
            setIsApproving(false);
        }
    };

    // Optimistically reflect that a new round started so the progress bar shows
    // immediately and polling kicks in, even before the backend flips to IN_PROGRESS.
    const markRegenerating = () => {
        setShowFeedbackForm(false);
        setFeedback('');
        setSelectedSampleUri(null);
        setRegenAt(Date.now());
        setAd((prev: any) => (prev ? { ...prev, currentState: 'IN_PROGRESS', samples: [] } : prev));
    };

    // Tweak the chosen sample (refine round), or regenerate a fresh batch.
    const submitFeedback = async (action: 'TWEAK' | 'REJECT_ALL') => {
        if (!ad) return;
        if (!feedback.trim()) {
            toast.error('Please describe what to change');
            return;
        }
        if (action === 'TWEAK' && hasSamples && !selectedSampleUri) {
            toast.error('Pick a sample to tweak');
            return;
        }
        setIsRejecting(true);
        try {
            if (hasSamples) {
                await reviewAdAction(ad.adId, action, {
                    feedback: feedback.trim(),
                    chosenSampleS3Uri: action === 'TWEAK' ? selectedSampleUri! : undefined,
                });
            } else {
                await reviewAd(ad.adId, false, feedback.trim());
            }
            toast.success(action === 'TWEAK' ? 'Tweaking your selection...' : 'Regenerating a new batch...');
            markRegenerating();
        } catch (error: any) {
            console.error('Failed to submit feedback:', error);
            toast.error(error.message || 'Failed to submit feedback');
        } finally {
            setIsRejecting(false);
        }
    };

    // Backward-compatible single-image reject (used when there are no samples).
    const handleReject = () => submitFeedback('REJECT_ALL');

    const handleDelete = async () => {
        if (!ad) return;
        setIsDeleting(true);
        try {
            await deleteAd(ad.adId);
            toast.success(`"${ad.name}" deleted`);
            navigate('/ads');
        } catch (error: any) {
            console.error('Failed to delete ad:', error);
            toast.error(error.message || 'Failed to delete ad');
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Determine if the ad can be deleted (not already deleting or in a transient state)
    const canDelete = ad && ad.currentState !== 'DELETING' && ad.currentState !== 'IN_PROGRESS' && ad.currentState !== 'IN_PROGESS';

    if (isLoading) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <p style={{ color: '#6b7280' }}>Loading ad details...</p>
                </div>
            </PageSection>
        );
    }

    if (error || !ad) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={{ padding: '24px' }}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/ads')}
                        type="button"
                    >
                        <ArrowLeftIcon /> Back to Ads
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <p style={{ color: '#6b7280', fontSize: '16px' }}>Ad not found.</p>
                    </div>
                </div>
            </PageSection>
        );
    }

    // When the round produced samples, the "main" image is the selected sample.
    const selectedSample = hasSamples
        ? (samples.find((s) => s.s3Uri === selectedSampleUri) || samples[0])
        : null;
    const mediaUrl = (selectedSample && selectedSample.presignedUrl) || ad.presignedUrl || ad.imageUrl;
    const hasImage = mediaUrl && mediaUrl.trim() !== '';
    const stateConfig = getAdStateConfig(ad.currentState || ad.status);
    const typeConfig = getAdTypeConfig(ad.adType);
    const isInProgress = ad.currentState === 'IN_PROGRESS' || ad.currentState === 'IN_PROGESS'
        || (regenAt !== null && Date.now() - regenAt < REGEN_GRACE_MS);
    const stepConfig = getGenerationStepConfig(ad.currentGenerationStep);
    const stepProgress = getStepProgress(ad.currentGenerationStep);
    const hasActiveStep = ad.currentGenerationStep && isActiveGenerationStep(ad.currentGenerationStep);

    const getGradientFromName = (name: string) => {
        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'linear-gradient(135deg, #667eea 0%, #f093fb 100%)',
            'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)'
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    const placeholderGradient = getGradientFromName(ad.name || 'Ad');
    const adInitial = (ad.name || 'A').charAt(0).toUpperCase();

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc', padding: 0 }}>
            {/* Page Header */}
            <div style={styles.pageHeader}>
                <div style={styles.pageHeaderInner}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/ads')}
                        type="button"
                    >
                        <ArrowLeftIcon /> Back to Ads
                    </button>
                    <div style={styles.titleRow}>
                        <div>
                            <h1 style={styles.pageTitle}>{ad.name}</h1>
                            <div style={styles.badgeRow}>
                                <span style={{
                                    ...styles.badge,
                                    backgroundColor: stateConfig.color.bg,
                                    color: stateConfig.color.text
                                }}>
                                    {stateConfig.label}
                                </span>
                                {ad.adType && (
                                    <span style={{
                                        ...styles.badge,
                                        backgroundColor: typeConfig.color.bg,
                                        color: typeConfig.color.text
                                    }}>
                                        {typeConfig.label}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={styles.titleActions}>
                            <button
                                style={styles.editButton}
                                onClick={handleEditClick}
                                title="Edit ad"
                                type="button"
                            >
                                <PencilAltIcon /> Edit
                            </button>
                            {canDelete && (
                                <button
                                    style={styles.deleteButton}
                                    onClick={() => setShowDeleteConfirm(true)}
                                    title="Delete ad"
                                    type="button"
                                >
                                    <TrashIcon /> Delete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Failure Reason Alert */}
            {ad.failureReason && (ad.currentState === 'FAILED' || ad.currentState === 'MODERATION_FAILED') && (
                <div style={{ padding: '0 32px' }}>
                    <div style={{
                        ...styles.failureAlert,
                        backgroundColor: ad.currentState === 'MODERATION_FAILED' ? '#fff7ed' : '#fef2f2',
                        border: ad.currentState === 'MODERATION_FAILED' ? '1px solid #fed7aa' : '1px solid #fecaca'
                    }}>
                        <p style={{
                            ...styles.failureAlertTitle,
                            color: ad.currentState === 'MODERATION_FAILED' ? '#c2410c' : '#dc2626'
                        }}>
                            {ad.currentState === 'MODERATION_FAILED' ? 'Content Moderation Issue' : 'Generation Failed'}
                        </p>
                        <p style={{
                            ...styles.failureAlertText,
                            color: ad.currentState === 'MODERATION_FAILED' ? '#9a3412' : '#991b1b'
                        }}>
                            {ad.failureReason}
                        </p>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div style={styles.contentArea}>
                {/* In-Progress Generation Banner */}
                {isInProgress && (
                    <>
                        <style>{`
                            @keyframes detailShimmer {
                                0% { background-position: -200% 0; }
                                100% { background-position: 200% 0; }
                            }
                            @keyframes detailPulseGlow {
                                0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.1); }
                                50% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.5), 0 0 60px rgba(139, 92, 246, 0.2); }
                            }
                            @keyframes detailSpin {
                                to { transform: rotate(360deg); }
                            }
                            @keyframes detailProgressPulse {
                                0%, 100% { opacity: 1; }
                                50% { opacity: 0.7; }
                            }
                        `}</style>
                        <div style={{
                            borderRadius: '12px',
                            padding: '28px',
                            marginBottom: '32px',
                            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                            animation: 'detailPulseGlow 2s ease-in-out infinite',
                            position: 'relative' as const,
                            overflow: 'hidden'
                        }}>
                            {/* Animated shimmer overlay */}
                            <div style={{
                                position: 'absolute' as const,
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.15) 50%, transparent 100%)',
                                backgroundSize: '200% 100%',
                                animation: 'detailShimmer 2s linear infinite',
                                pointerEvents: 'none' as const
                            }} />

                            <div style={{ position: 'relative' as const, zIndex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        border: '3px solid rgba(255, 255, 255, 0.3)',
                                        borderTopColor: '#a78bfa',
                                        borderRadius: '50%',
                                        animation: 'detailSpin 1s linear infinite'
                                    }} />
                                    <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: 700 }}>
                                        Generating Your Ad
                                    </span>
                                </div>

                                {hasActiveStep && stepConfig && stepProgress && (
                                    <>
                                        <p style={{ color: '#c4b5fd', fontSize: '14px', margin: '0 0 14px 0' }}>
                                            {stepConfig.label}...
                                        </p>
                                        <div style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                            borderRadius: '6px',
                                            height: '8px',
                                            overflow: 'hidden',
                                            marginBottom: '8px'
                                        }}>
                                            <div style={{
                                                backgroundColor: '#a78bfa',
                                                height: '100%',
                                                borderRadius: '6px',
                                                transition: 'width 0.5s ease',
                                                width: `${stepProgress.percentage}%`,
                                                animation: 'detailProgressPulse 1.5s ease-in-out infinite'
                                            }} />
                                        </div>
                                        <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                                            Step {stepProgress.current} of {stepProgress.total}
                                        </span>
                                    </>
                                )}

                                {!hasActiveStep && (
                                    <p style={{ color: '#c4b5fd', fontSize: '14px', margin: 0 }}>
                                        Your ad is being created. This may take a moment...
                                    </p>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Media Section - hide placeholder when in progress. The ad
                    (selected AI sample or uploaded media) previews on the car. */}
                {(hasImage || !isInProgress) && (
                    <div style={hasImage ? styles.mediaSectionCar : styles.mediaSection}>
                        {hasImage ? (
                            <LedCarPreview
                                mediaUrl={mediaUrl}
                                mediaType={/\.(mp4|webm|mov)(\?|$)/i.test(mediaUrl || '') ? 'VIDEO' : 'IMAGE'}
                                valid={true}
                                maxWidth={760}
                            />
                        ) : (
                            <div style={{ ...styles.placeholderMedia, background: placeholderGradient }}>
                                <span style={styles.placeholderInitial}>{adInitial}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Review Section - only for PENDING_REVIEW ads */}
                {ad.currentState === 'PENDING_REVIEW' && (() => {
                    const currentIteration = iterationData?.currentIteration || ad?.iterationCount || 1;
                    const maxIterations = iterationData?.maxIterations || ad?.maxIterations || 5;
                    const isAtMaxIterations = currentIteration >= maxIterations;
                    const isReviewLoading = isApproving || isRejecting;

                    return (
                        <div style={styles.reviewSection}>
                            <div style={styles.reviewHeader}>
                                <h2 style={styles.reviewTitle}>Review AI Ad</h2>
                                <span style={styles.iterationBadge}>
                                    Iteration {currentIteration} of {maxIterations}
                                </span>
                            </div>
                            <p style={styles.reviewSubtitle}>
                                {hasSamples
                                    ? 'Pick the sample you like best, then approve it or tweak it'
                                    : 'Review the generated image before publishing'}
                            </p>

                            {/* Sample picker — select one of the round's candidates */}
                            {hasSamples && (
                                <div style={styles.sampleGallery}>
                                    {samples.map((s) => {
                                        const isSel = s.s3Uri === selectedSampleUri;
                                        return (
                                            <button
                                                key={s.sampleIndex}
                                                type="button"
                                                onClick={() => setSelectedSampleUri(s.s3Uri)}
                                                style={{
                                                    ...styles.sampleThumbBtn,
                                                    ...(isSel ? styles.sampleThumbBtnSelected : {})
                                                }}
                                                aria-label={`Select sample ${s.sampleIndex + 1}`}
                                            >
                                                {s.presignedUrl ? (
                                                    <img src={s.presignedUrl} alt={`Sample ${s.sampleIndex + 1}`} style={styles.sampleThumbImg} />
                                                ) : (
                                                    <div style={styles.sampleThumbEmpty}>No image</div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Max iterations warning */}
                            {isAtMaxIterations && (
                                <div style={styles.maxIterationsWarning}>
                                    Maximum iterations reached ({maxIterations}/{maxIterations}).
                                    You can approve this version or delete the ad and start over.
                                </div>
                            )}

                            {/* Action Buttons */}
                            {!showFeedbackForm ? (
                                <div style={styles.reviewActionsRow}>
                                    <button
                                        style={{
                                            ...styles.approveButton,
                                            ...(isReviewLoading ? styles.disabledButton : {})
                                        }}
                                        onClick={handleApprove}
                                        disabled={isReviewLoading}
                                        type="button"
                                    >
                                        {isApproving ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <CheckCircleIcon />
                                        )}
                                        {isApproving ? 'Approving...' : (hasSamples ? 'Approve selected' : 'Approve')}
                                    </button>
                                    {!isAtMaxIterations && (
                                        <button
                                            style={{
                                                ...styles.reviseButton,
                                                ...(isReviewLoading ? styles.disabledButton : {})
                                            }}
                                            onClick={() => {
                                                setShowFeedbackForm(true);
                                                setSelectedHistoryImage(null);
                                            }}
                                            disabled={isReviewLoading}
                                            type="button"
                                        >
                                            <AutomationIcon />
                                            Revise & Regenerate
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div style={styles.feedbackForm}>
                                    <div style={styles.feedbackFormLabel}>
                                        <div style={styles.feedbackFormIcon}>
                                            <AutomationIcon />
                                        </div>
                                        <p style={styles.feedbackFormTitle}>What would you like to change?</p>
                                    </div>

                                    <textarea
                                        style={styles.textarea}
                                        placeholder="e.g. Make the colors more vibrant, enlarge the logo, use a darker background..."
                                        value={feedback}
                                        onChange={(e) => handleFeedbackChange(e.target.value)}
                                    />

                                    <div style={styles.charCounter}>
                                        <span style={{ fontSize: '12px', color: feedback.length > 900 ? '#f59e0b' : '#9ca3af' }}>
                                            {feedback.length}/1000 characters
                                        </span>
                                    </div>

                                    {hasSamples ? (
                                        <div style={styles.reviewActionsRow}>
                                            <button
                                                style={{
                                                    ...styles.submitFeedbackButton,
                                                    ...(isReviewLoading || !feedback.trim() ? styles.disabledButton : {})
                                                }}
                                                onClick={() => submitFeedback('TWEAK')}
                                                disabled={isReviewLoading || !feedback.trim()}
                                                type="button"
                                            >
                                                {isRejecting ? <Spinner size="sm" /> : <AutomationIcon />}
                                                {isRejecting ? 'Working...' : 'Tweak selected'}
                                            </button>
                                            <button
                                                style={{
                                                    ...styles.reviseButton,
                                                    ...(isReviewLoading || !feedback.trim() ? styles.disabledButton : {})
                                                }}
                                                onClick={() => submitFeedback('REJECT_ALL')}
                                                disabled={isReviewLoading || !feedback.trim()}
                                                type="button"
                                            >
                                                <AutomationIcon />
                                                Regenerate all
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            style={{
                                                ...styles.submitFeedbackButton,
                                                ...(isReviewLoading || !feedback.trim() ? styles.disabledButton : {})
                                            }}
                                            onClick={handleReject}
                                            disabled={isReviewLoading || !feedback.trim()}
                                            type="button"
                                        >
                                            {isRejecting ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <AutomationIcon />
                                            )}
                                            {isRejecting ? 'Regenerating...' : 'Submit Feedback & Regenerate'}
                                        </button>
                                    )}

                                    <button
                                        style={styles.cancelLink}
                                        onClick={() => setShowFeedbackForm(false)}
                                        disabled={isReviewLoading}
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* AI Generation Section - only for AI ads */}
                {ad.adType === 'GENERATE_WITH_AI' && (
                    <div style={styles.aiSection}>
                        <h2 style={styles.sectionTitle}>
                            <AutomationIcon /> AI Generation
                        </h2>
                        {ad.prompt && (
                            <div style={styles.promptBlock}>
                                <span style={styles.promptBlockLabel}>Ad Description</span>
                                <p style={styles.promptBlockText}>{ad.prompt}</p>
                            </div>
                        )}
                        <div style={styles.infoGrid}>
                            {ad.iterationCount !== undefined && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Iteration</span>
                                    <span style={styles.infoValue}>
                                        {ad.iterationCount}{ad.maxIterations ? ` of ${ad.maxIterations}` : ''}
                                    </span>
                                </div>
                            )}
                            {ad.currentGenerationStep && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Generation Step</span>
                                    <span style={styles.infoValue}>{ad.currentGenerationStep.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase())}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Iteration History Section - for AI ads with iterations */}
                {iterationData && iterationData.iterations.length > 0 && (
                    <div style={styles.iterationSection}>
                        <h2 style={styles.sectionTitle}>
                            <HistoryIcon /> Iteration History
                        </h2>
                        <div style={styles.iterationTimeline}>
                            {iterationData.iterations.map((item, index) => (
                                <div key={item.iterationNumber} style={styles.iterationEntry}>
                                    <div style={styles.iterationEntryLeft}>
                                        <div style={{
                                            ...styles.iterationDot,
                                            background: index === iterationData!.iterations.length - 1
                                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                : '#d1d5db'
                                        }} />
                                        {index < iterationData!.iterations.length - 1 && (
                                            <div style={styles.iterationLine} />
                                        )}
                                    </div>
                                    <div style={styles.iterationEntryContent}>
                                        <div style={styles.iterationEntryHeader}>
                                            <span style={styles.iterationEntryVersion}>Revision #{item.iterationNumber}</span>
                                            {item.createdAt && (
                                                <span style={styles.iterationEntryDate}>
                                                    {new Date(item.createdAt).toLocaleDateString('en-US', {
                                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                    })}
                                                </span>
                                            )}
                                        </div>
                                        {item.samples && item.samples.length > 0 ? (
                                            <div style={styles.sampleGallery}>
                                                {item.samples.map((s) => (
                                                    <div key={s.sampleIndex} style={styles.iterationSampleItem}>
                                                        {s.presignedUrl ? (
                                                            <button
                                                                type="button"
                                                                style={styles.iterationSampleThumb}
                                                                onClick={() => setPreviewSample({ ...s, iterationNumber: item.iterationNumber })}
                                                                title="Click to preview full size"
                                                                aria-label={`Preview Revision #${item.iterationNumber} option ${s.sampleIndex + 1}`}
                                                            >
                                                                <img
                                                                    src={s.presignedUrl}
                                                                    alt={`Revision #${item.iterationNumber} option ${s.sampleIndex + 1}`}
                                                                    style={styles.iterationSampleThumbImg}
                                                                />
                                                                <span style={styles.iterationSampleExpand}>
                                                                    <SearchPlusIcon />
                                                                </span>
                                                            </button>
                                                        ) : (
                                                            <div style={{ ...styles.iterationSampleImg, ...styles.sampleThumbEmpty }}>No image</div>
                                                        )}
                                                        {ad.currentState === 'PENDING_REVIEW' && s.s3Uri && (
                                                            <button
                                                                type="button"
                                                                style={{
                                                                    ...styles.acceptVersionButton,
                                                                    ...((isApproving || isRejecting) ? styles.disabledButton : {})
                                                                }}
                                                                onClick={() => handleApproveRevision(s.s3Uri, item.iterationNumber)}
                                                                disabled={isApproving || isRejecting}
                                                            >
                                                                <CheckCircleIcon />
                                                                Accept this version
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : item.imageUrl && (
                                            <img
                                                src={item.imageUrl}
                                                alt={`Revision #${item.iterationNumber}`}
                                                style={styles.iterationEntryImage}
                                            />
                                        )}
                                        {item.feedback && (
                                            <div style={styles.iterationEntryFeedback}>
                                                <span style={styles.iterationFeedbackLabel}>Feedback</span>
                                                <p style={styles.iterationFeedbackText}>{item.feedback}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Details Grid */}
                <div style={styles.infoSection}>
                    <h2 style={styles.sectionTitle}>Details</h2>
                    <div style={styles.infoGrid}>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <BuildingIcon /> Organization
                            </span>
                            <span style={styles.infoValue}>
                                {ad.organizationName || `Organization ${ad.organizationId || 'Unknown'}`}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <UserIcon /> Creator
                            </span>
                            <span style={styles.infoValue}>
                                {ad.creatorName || 'Unknown'}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                Ad Type
                            </span>
                            <span style={styles.infoValue}>
                                {ad.adType === 'GENERATE_WITH_AI' ? 'AI Generated' : ad.adType === 'UPLOAD_MEDIA' ? 'Uploaded Media' : ad.adType || 'Unknown'}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                Status
                            </span>
                            <span style={styles.infoValue}>
                                {stateConfig.label}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <CalendarAltIcon /> Created
                            </span>
                            <span style={styles.infoValue}>
                                {formatDate(ad.createDateTime)}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <CalendarAltIcon /> Last Updated
                            </span>
                            <span style={styles.infoValue}>
                                {formatDate(ad.updateDateTime)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tags Section */}
                {ad.tags !== undefined && (
                    <div style={styles.tagsSection}>
                        <h2 style={styles.sectionTitle}>
                            <TagIcon /> Tags
                        </h2>
                        {ad.tags && ad.tags.length > 0 ? (
                            <div style={styles.tagsList}>
                                {ad.tags.map((tag: string, index: number) => (
                                    <span key={index} style={styles.tag}>{tag}</span>
                                ))}
                            </div>
                        ) : (
                            <span style={styles.noTags}>No tags</span>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div style={styles.footer}>
                    <span style={styles.adId}>ID: {ad.adId}</span>
                </div>
            </div>

            {/* Edit Ad Modal */}
            <EditAd
                ad={ad}
                isOpen={editModalOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            <AppModal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} width="sm">
                <div style={{ padding: '40px 32px 28px' }}>
                    <LogoBadge size={56} style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', color: '#dc2626' }}>
                        <TrashIcon style={{ fontSize: 26, color: '#dc2626' }} />
                    </LogoBadge>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 700, color: colors.title, textAlign: 'center', letterSpacing: '-0.5px' }}>
                        Delete ad?
                    </h3>
                    <p style={{ margin: '0 0 24px 0', color: colors.subtle, fontSize: '14px', lineHeight: 1.6, textAlign: 'center' }}>
                        Are you sure you want to delete &quot;{ad.name}&quot;? This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <GradientButton
                            appearance="secondary"
                            onClick={() => setShowDeleteConfirm(false)}
                            isDisabled={isDeleting}
                        >
                            Cancel
                        </GradientButton>
                        <GradientButton
                            appearance="danger"
                            onClick={handleDelete}
                            isDisabled={isDeleting}
                            isLoading={isDeleting}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </GradientButton>
                    </div>
                </div>
            </AppModal>

            {/* Lightbox: preview a past-round sample full size, then accept it. */}
            <AppModal isOpen={!!previewSample} onClose={() => setPreviewSample(null)} width="xl">
                {previewSample && (
                    <div style={styles.previewModalBody}>
                        <h3 style={styles.previewModalTitle}>
                            Revision #{previewSample.iterationNumber}
                            <span style={styles.previewModalSub}> · Option {previewSample.sampleIndex + 1}</span>
                        </h3>
                        {previewSample.presignedUrl && (
                            <img
                                src={previewSample.presignedUrl}
                                alt={`Revision #${previewSample.iterationNumber}`}
                                style={styles.previewModalImage}
                            />
                        )}
                        {ad.currentState === 'PENDING_REVIEW' && (
                            <button
                                type="button"
                                style={{
                                    ...styles.previewAcceptButton,
                                    ...((isApproving || isRejecting) ? styles.disabledButton : {})
                                }}
                                onClick={() => handleApproveRevision(previewSample.s3Uri, previewSample.iterationNumber, { skipConfirm: true })}
                                disabled={isApproving || isRejecting}
                            >
                                {isApproving ? <Spinner size="sm" /> : <CheckCircleIcon />}
                                {isApproving ? 'Approving...' : 'Accept this version'}
                            </button>
                        )}
                    </div>
                )}
            </AppModal>
        </PageSection>
    );
};

const styles: Record<string, React.CSSProperties> = {
    pageHeader: {
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
    },
    pageHeaderInner: {
        maxWidth: '100%'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 0',
        fontSize: '14px',
        fontWeight: 500,
        border: 'none',
        background: 'none',
        color: '#667eea',
        cursor: 'pointer',
        marginBottom: '16px',
        transition: 'color 0.2s'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
    },
    pageTitle: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    titleActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0
    },
    editButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'opacity 0.2s'
    },
    deleteButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: '#ffffff',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        padding: '8px 18px',
        cursor: 'pointer',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'all 0.2s'
    },
    contentArea: {
        padding: '32px'
    },
    mediaSection: {
        position: 'relative',
        width: '100%',
        maxHeight: '500px',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
        borderRadius: '12px',
        marginBottom: '32px'
    },
    mediaSectionCar: {
        position: 'relative',
        width: '100%',
        marginBottom: '32px'
    } as React.CSSProperties,
    mediaImage: {
        width: '100%',
        maxHeight: '500px',
        objectFit: 'contain',
        display: 'block'
    },
    placeholderMedia: {
        width: '100%',
        height: '350px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px'
    },
    placeholderInitial: {
        fontSize: '140px',
        fontWeight: 800,
        color: 'rgba(255, 255, 255, 0.25)',
        textTransform: 'uppercase',
        userSelect: 'none'
    },
    badgeRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    badge: {
        padding: '6px 14px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    failureAlert: {
        padding: '14px 16px',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '24px'
    },
    failureAlertTitle: {
        fontSize: '14px',
        fontWeight: 700,
        margin: 0
    },
    failureAlertText: {
        fontSize: '13px',
        lineHeight: 1.5,
        margin: 0
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#374151',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    infoSection: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px'
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    infoLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    infoValue: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1f2937'
    },
    tagsSection: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    tagsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    tag: {
        padding: '6px 14px',
        fontSize: '13px',
        fontWeight: 500,
        borderRadius: '16px',
        backgroundColor: '#f3f4f6',
        color: '#4b5563'
    },
    noTags: {
        fontSize: '13px',
        color: '#9ca3af',
        fontStyle: 'italic'
    },
    footer: {
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb'
    },
    adId: {
        fontSize: '12px',
        color: '#9ca3af',
        fontFamily: 'monospace'
    },
    // Iteration History section styles
    iterationSection: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    iterationTimeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0'
    },
    iterationEntry: {
        display: 'flex',
        gap: '16px'
    },
    iterationEntryLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        width: '20px'
    },
    iterationDot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        flexShrink: 0
    },
    iterationLine: {
        width: '2px',
        flex: 1,
        background: '#e5e7eb',
        marginTop: '4px',
        marginBottom: '4px'
    },
    iterationEntryContent: {
        flex: 1,
        paddingBottom: '24px'
    },
    iterationEntryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px'
    },
    iterationEntryVersion: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#1f2937'
    },
    iterationEntryDate: {
        fontSize: '12px',
        color: '#9ca3af'
    },
    iterationEntryImage: {
        maxWidth: '240px',
        maxHeight: '140px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        objectFit: 'cover' as const,
        display: 'block',
        marginBottom: '8px'
    },
    iterationSampleItem: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '6px',
        width: '140px'
    },
    iterationSampleImg: {
        width: '140px',
        height: '88px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        objectFit: 'cover' as const,
        display: 'block',
        background: '#0f172a'
    },
    iterationSampleThumb: {
        position: 'relative' as const,
        padding: 0,
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'zoom-in',
        background: '#0f172a',
        width: '140px',
        height: '88px',
        display: 'block'
    },
    iterationSampleThumbImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        display: 'block'
    },
    iterationSampleExpand: {
        position: 'absolute' as const,
        bottom: '4px',
        right: '4px',
        background: 'rgba(15, 23, 42, 0.65)',
        color: '#ffffff',
        borderRadius: '6px',
        padding: '2px 5px',
        fontSize: '11px',
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none' as const
    },
    previewModalBody: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        alignItems: 'center'
    },
    previewModalTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#1f2937',
        margin: 0,
        alignSelf: 'flex-start'
    },
    previewModalSub: {
        fontWeight: 400,
        color: '#9ca3af'
    },
    previewModalImage: {
        maxWidth: '100%',
        maxHeight: '70vh',
        borderRadius: '10px',
        border: '1px solid #e5e7eb',
        objectFit: 'contain' as const,
        display: 'block'
    },
    previewAcceptButton: {
        padding: '12px 28px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
    },
    acceptVersionButton: {
        padding: '7px 10px',
        fontSize: '12px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
    },
    iterationEntryFeedback: {
        background: '#f9fafb',
        borderRadius: '8px',
        padding: '10px 12px',
        border: '1px solid #f3f4f6'
    },
    iterationFeedbackLabel: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'block',
        marginBottom: '4px'
    },
    iterationFeedbackText: {
        fontSize: '13px',
        color: '#374151',
        lineHeight: 1.5,
        margin: 0
    },
    // AI Generation section styles
    aiSection: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    promptBlock: {
        background: '#f5f3ff',
        border: '1px solid #e9e5ff',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '16px'
    },
    promptBlockLabel: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#7c3aed',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '6px',
        display: 'block'
    },
    promptBlockText: {
        fontSize: '14px',
        color: '#1f2937',
        lineHeight: 1.6,
        margin: 0,
        whiteSpace: 'pre-wrap'
    },
    // Review section styles
    reviewSection: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '2px solid #f59e0b',
        padding: '24px',
        marginBottom: '24px'
    },
    reviewHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '4px',
        flexWrap: 'wrap'
    },
    reviewTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1f2937',
        margin: 0
    },
    reviewSubtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 16px 0'
    },
    sampleGallery: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap' as const,
        marginBottom: '16px'
    },
    sampleThumbBtn: {
        padding: 0,
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
        overflow: 'hidden',
        cursor: 'pointer',
        background: '#0f172a',
        width: '140px',
        height: '88px',
        transition: 'border-color 0.15s, box-shadow 0.15s'
    } as React.CSSProperties,
    sampleThumbBtnSelected: {
        borderColor: '#8b5cf6',
        boxShadow: '0 0 0 3px rgba(139,92,246,0.25)'
    } as React.CSSProperties,
    sampleThumbImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        display: 'block'
    },
    sampleThumbEmpty: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#9ca3af',
        fontSize: '12px'
    },
    iterationBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: '#ffffff',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 600
    },
    promptUsed: {
        fontSize: '13px',
        color: '#6b7280',
        margin: '0 0 16px 0',
        lineHeight: 1.5
    },
    promptLabel: {
        fontWeight: 600,
        color: '#374151'
    },
    historyToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 14px',
        background: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 600,
        color: '#374151',
        marginBottom: '16px',
        width: '100%',
        transition: 'background 0.2s'
    },
    historySection: {
        marginBottom: '16px',
        padding: '14px',
        background: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
    },
    historyScroll: {
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        paddingBottom: '8px'
    },
    historyItem: {
        flexShrink: 0,
        width: '120px',
        cursor: 'pointer'
    },
    historyThumb: {
        width: '120px',
        height: '70px',
        borderRadius: '8px',
        objectFit: 'cover' as const,
        border: '2px solid #e5e7eb',
        display: 'block',
        transition: 'border-color 0.2s'
    },
    historyLabel: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#6b7280',
        marginTop: '4px'
    },
    historyFeedback: {
        fontSize: '11px',
        color: '#9ca3af',
        marginTop: '2px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '120px'
    },
    historyNoThumb: {
        width: '120px',
        height: '70px',
        borderRadius: '8px',
        border: '2px dashed #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        color: '#9ca3af',
        background: '#f3f4f6'
    },
    maxIterationsWarning: {
        padding: '12px 16px',
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: '10px',
        fontSize: '13px',
        color: '#92400e',
        lineHeight: 1.5,
        marginBottom: '16px'
    },
    reviewActionsRow: {
        display: 'flex',
        gap: '12px'
    },
    approveButton: {
        flex: 1,
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
    },
    reviseButton: {
        flex: 1,
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: '2px solid #f59e0b',
        borderRadius: '10px',
        background: '#ffffff',
        color: '#d97706',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
    },
    disabledButton: {
        opacity: 0.6,
        cursor: 'not-allowed'
    },
    feedbackForm: {
        padding: '16px',
        background: '#fffbeb',
        borderRadius: '12px',
        border: '1px solid #fde68a'
    },
    feedbackFormLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
    },
    feedbackFormIcon: {
        width: '28px',
        height: '28px',
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '14px'
    },
    feedbackFormTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#374151',
        margin: 0
    },
    textarea: {
        width: '100%',
        minHeight: '100px',
        padding: '12px',
        fontSize: '14px',
        backgroundColor: '#ffffff',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        outline: 'none',
        resize: 'vertical' as const,
        fontFamily: 'inherit',
        lineHeight: 1.5,
        transition: 'border-color 0.2s',
        boxSizing: 'border-box' as const
    },
    charCounter: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '6px'
    },
    submitFeedbackButton: {
        width: '100%',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
    },
    cancelLink: {
        width: '100%',
        padding: '8px',
        fontSize: '13px',
        fontWeight: 500,
        border: 'none',
        background: 'none',
        color: '#6b7280',
        cursor: 'pointer',
        textAlign: 'center' as const,
        marginTop: '8px'
    },
    // Delete confirmation modal styles
    confirmOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
    },
    confirmModal: {
        background: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    confirmTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0'
    },
    confirmText: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 20px 0',
        lineHeight: 1.5
    },
    confirmButtons: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
    },
    confirmCancelButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 500,
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        color: '#374151',
        cursor: 'pointer'
    },
    confirmDeleteButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '8px',
        background: '#dc2626',
        color: '#ffffff',
        cursor: 'pointer'
    }
};

export { AdDetailPage };
